import { GlobalWorkerOptions, getDocument, version } from 'pdfjs-dist';
// Em ambientes Vite 5 + pdfjs-dist v4, preferimos apontar o worker para a CDN
// usando a versão do pacote, evitando problemas de resolução local do arquivo.
GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${version}/build/pdf.worker.min.mjs`;

export async function extractPdfText(arrayBuffer: ArrayBuffer): Promise<string> {
	const loadingTask = getDocument({ data: arrayBuffer });
	const pdf = await loadingTask.promise;
	try {
		let fullText = '';
		for (let pageNum = 1; pageNum <= pdf.numPages; pageNum += 1) {
			// getPage pode lançar, manter isolado
			const page = await pdf.getPage(pageNum);
			const content = await page.getTextContent();
			const pageText = (content.items as any[])
				.map((item) => {
					const s = (item && (item.str || item.text)) || '';
					return typeof s === 'string' ? s : '';
				})
				.filter(Boolean)
				.join(' ');
			fullText += (fullText ? '\n' : '') + pageText;
		}
		return fullText;
	} finally {
		await pdf.destroy();
	}
}

export async function extractEmployeeNamesFromPdf(arrayBuffer: ArrayBuffer): Promise<string[]> {
	// Estratégia:
	// - Para cada página, agrupar textos por linha (y ~ tolerância)
	// - Em cada linha, localizar o rótulo "Nome" (case-insensitive), aceitando "Nome:"
	// - Capturar apenas a primeira ocorrência por página (primeira via) e pular para a próxima página
	// - Valor: texto à direita do rótulo na mesma linha; se vazio, procurar na linha seguinte

	const loadingTask = getDocument({ data: arrayBuffer });
	const pdf = await loadingTask.promise;
	const nomesEncontrados: string[] = [];

	const toLower = (s: string) => (s || '').toLowerCase();

	for (let pageNum = 1; pageNum <= pdf.numPages; pageNum += 1) {
		let capturedForPage: string | null = null;
		const page = await pdf.getPage(pageNum);
		const content = await page.getTextContent();
		const items = (content.items as any[])
			.map((it) => {
				const str: string = (it && (it.str || it.text)) || '';
				const tm: number[] = (it && (it.transform || it.transform)) || [];
				// Em PDF.js, e = tm[4], f = tm[5]
				const x: number = Array.isArray(tm) && tm.length >= 6 ? Number(tm[4]) : 0;
				const y: number = Array.isArray(tm) && tm.length >= 6 ? Number(tm[5]) : 0;
				const w: number = typeof it.width === 'number' ? it.width : Math.max(0, str.length * 4);
				return { str, x, y, w };
			})
			.filter((it) => !!it.str);

		// Agrupar por linhas (tolerância em Y)
		const tolY = 2.5;
		const lines: { y: number; spans: { str: string; x: number; y: number; w: number }[] }[] = [];
		for (const it of items) {
			let group = lines.find((ln) => Math.abs(ln.y - it.y) <= tolY);
			if (!group) {
				group = { y: it.y, spans: [] };
				lines.push(group);
			}
			group.spans.push(it);
		}

		// Ordenar linhas por posição vertical e spans por X
		lines.sort((a, b) => b.y - a.y);
		for (const ln of lines) ln.spans.sort((a, b) => a.x - b.x);

		for (let i = 0; i < lines.length; i += 1) {
			if (capturedForPage) break;
			const line = lines[i];
			// Procurar rótulo "Nome" em algum span
			let labelSpanIdx = -1;
			for (let s = 0; s < line.spans.length; s += 1) {
				const span = line.spans[s];
				const lower = toLower(span.str);
				const idx = lower.indexOf('nome');
				// garantir que seja palavra (evitar "sobrenome")
				if (idx >= 0) {
					const before = idx === 0 ? ' ' : lower[idx - 1];
					const after = idx + 4 < lower.length ? lower[idx + 4] : ' ';
					const isBoundaryBefore = !/[a-zá-ú]/.test(before);
					const isBoundaryAfter = !/[a-zá-ú]/.test(after);
					if (isBoundaryBefore && isBoundaryAfter) {
						labelSpanIdx = s;
						break;
					}
				}
			}
			if (labelSpanIdx < 0) continue;

			const labelSpan = line.spans[labelSpanIdx];

			// Tratar "Nome" como cabeçalho de coluna: definir faixa horizontal [bandLeft, bandRight)
			const bandLeft = labelSpan.x - 2;
			// Próximo cabeçalho na mesma linha (ex.: "CBO") determina o limite direito
			const nextHeaderSpan = line.spans[labelSpanIdx + 1];
			const bandRight = nextHeaderSpan ? nextHeaderSpan.x - 2 : (labelSpan.x + labelSpan.w + 220);

			// Percorrer algumas linhas abaixo até achar a primeira com conteúdo nessa faixa
			const maxLookAhead = 6;
			for (let look = 1; look <= maxLookAhead && !capturedForPage; look += 1) {
				const lnBelow = lines[i + look];
				if (!lnBelow) break;
				const pieces = lnBelow.spans
					.filter((sp) => sp.x >= bandLeft && sp.x < bandRight)
					.sort((a, b) => a.x - b.x)
					.map((sp) => sp.str);
				let candidate = pieces.join(' ').replace(/\s+/g, ' ').trim();

				// Ignorar tokens típicos de cabeçalho/ruído
				const ignoreTokens = ['cbo', 'cpf', 'cnpj', 'competência', 'competencia', 'data', 'setor/local'];
				if (ignoreTokens.includes(toLower(candidate))) {
					candidate = '';
				}
				// Preferir nomes "completos": pelo menos um espaço e com letras
				if (candidate && (candidate.includes(' ') || candidate.length >= 8)) {
					capturedForPage = candidate;
					break;
				}
			}
		}

		if (capturedForPage) {
			// Normalizar espaços e aparar
			const nome = capturedForPage.replace(/\s+/g, ' ').trim();
			if (nome) nomesEncontrados.push(nome);
		}
	}

	await pdf.destroy();
	return nomesEncontrados;
}

export async function extractEmployeeNamesByPage(arrayBuffer: ArrayBuffer): Promise<Array<string | null>> {
	const loadingTask = getDocument({ data: arrayBuffer });
	const pdf = await loadingTask.promise;
	const nomes: Array<string | null> = [];
	const toLower = (s: string) => (s || '').toLowerCase();

	try {
		for (let pageNum = 1; pageNum <= pdf.numPages; pageNum += 1) {
			let capturedForPage: string | null = null;
			const page = await pdf.getPage(pageNum);
			const content = await page.getTextContent();
			const items = (content.items as any[])
				.map((it) => {
					const str: string = (it && (it.str || it.text)) || '';
					const tm: number[] = (it && (it.transform || it.transform)) || [];
					const x: number = Array.isArray(tm) && tm.length >= 6 ? Number(tm[4]) : 0;
					const y: number = Array.isArray(tm) && tm.length >= 6 ? Number(tm[5]) : 0;
					const w: number = typeof it.width === 'number' ? it.width : Math.max(0, str.length * 4);
					return { str, x, y, w };
				})
				.filter((it) => !!it.str);

			const tolY = 2.5;
			const lines: { y: number; spans: { str: string; x: number; y: number; w: number }[] }[] = [];
			for (const it of items) {
				let group = lines.find((ln) => Math.abs(ln.y - it.y) <= tolY);
				if (!group) {
					group = { y: it.y, spans: [] };
					lines.push(group);
				}
				group.spans.push(it);
			}
			lines.sort((a, b) => b.y - a.y);
			for (const ln of lines) ln.spans.sort((a, b) => a.x - b.x);

			for (let i = 0; i < lines.length; i += 1) {
				if (capturedForPage) break;
				const line = lines[i];
				let labelSpanIdx = -1;
				for (let s = 0; s < line.spans.length; s += 1) {
					const span = line.spans[s];
					const lower = toLower(span.str);
					const idx = lower.indexOf('nome');
					if (idx >= 0) {
						const before = idx === 0 ? ' ' : lower[idx - 1];
						const after = idx + 4 < lower.length ? lower[idx + 4] : ' ';
						const isBoundaryBefore = !/[a-zá-ú]/.test(before);
						const isBoundaryAfter = !/[a-zá-ú]/.test(after);
						if (isBoundaryBefore && isBoundaryAfter) {
							labelSpanIdx = s;
							break;
						}
					}
				}
				if (labelSpanIdx < 0) continue;

				const labelSpan = line.spans[labelSpanIdx];
				const bandLeft = labelSpan.x - 2;
				const nextHeaderSpan = line.spans[labelSpanIdx + 1];
				const bandRight = nextHeaderSpan ? nextHeaderSpan.x - 2 : (labelSpan.x + labelSpan.w + 220);

				const maxLookAhead = 6;
				for (let look = 1; look <= maxLookAhead && !capturedForPage; look += 1) {
					const lnBelow = lines[i + look];
					if (!lnBelow) break;
					const pieces = lnBelow.spans
						.filter((sp) => sp.x >= bandLeft && sp.x < bandRight)
						.sort((a, b) => a.x - b.x)
						.map((sp) => sp.str);
					let candidate = pieces.join(' ').replace(/\s+/g, ' ').trim();
					const ignoreTokens = ['cbo', 'cpf', 'cnpj', 'competência', 'competencia', 'data', 'setor/local'];
					if (ignoreTokens.includes(toLower(candidate))) {
						candidate = '';
					}
					if (candidate && (candidate.includes(' ') || candidate.length >= 8)) {
						capturedForPage = candidate;
						break;
					}
				}
			}

			nomes.push(capturedForPage ? capturedForPage.replace(/\s+/g, ' ').trim() : null);
		}
		return nomes;
	} finally {
		await pdf.destroy();
	}
}


